// Cliente da API do Acervo, tipado a partir do contrato OpenAPI.
//
// - O access token fica só na memória (nunca no localStorage, onde um script
//   malicioso conseguiria lê-lo).
// - O refresh token fica no cookie HttpOnly que a API grava; ao recarregar a
//   página, restore() troca o cookie por um access token novo.
// - Se uma chamada voltar 401 porque o token venceu, o cliente renova a
//   sessão uma vez e repete a chamada, sem a tela perceber.

import createClient from "openapi-fetch";
import type { components, paths } from "./schema";

export type Schemas = components["schemas"];
export type User = Schemas["User"];
export type AuthResponse = Schemas["AuthResponse"];
export type Problem = Schemas["Problem"];
export type Upload = Schemas["Upload"];

/** Erro da API no formato RFC 9457, com o código estável do backend. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId?: string;

  constructor(status: number, problem: Partial<Problem> | undefined) {
    super(problem?.detail ?? `erro ${status}`);
    this.status = status;
    this.code = problem?.code ?? "unknown";
    this.requestId = problem?.request_id;
  }
}

export interface Session {
  accessToken: string;
  expiresAt: Date;
  user: User;
}

/**
 * Onde guardar o refresh token quando não há cookie (app da TV). No site,
 * deixe vazio: o refresh token fica no cookie HttpOnly.
 */
export interface TokenStore {
  get(): string | null;
  set(token: string | null): void;
}

export interface ApiOptions {
  baseUrl: string;
  /** Chamado quando a sessão começa, é renovada ou termina (null). */
  onSession?: (s: Session | null) => void;
  fetch?: typeof fetch;
  /** Modo TV: refresh token guardado no aparelho e enviado no corpo. */
  refreshStore?: TokenStore;
  /** Tipo e nome do aparelho, registrados na sessão. */
  device?: { kind: "web" | "webos" | "cli"; name: string };
}

const AUTH_PATHS = ["/v1/auth/login", "/v1/auth/refresh", "/v1/auth/mfa/", "/v1/auth/device/token"];

export function createApi(opts: ApiOptions) {
  const baseFetch = opts.fetch ?? globalThis.fetch.bind(globalThis);
  let session: Session | null = null;
  let refreshing: Promise<Session | null> | null = null;

  const store = opts.refreshStore;
  const setSession = (s: Session | null) => {
    session = s;
    if (!s) store?.set(null);
    opts.onSession?.(s);
  };

  const toSession = (r: AuthResponse): Session | null => {
    if (r.refresh_token) store?.set(r.refresh_token);
    return r.status === "ok" && r.access_token && r.user && r.expires_at
      ? { accessToken: r.access_token, expiresAt: new Date(r.expires_at), user: r.user }
      : null;
  };

  /** Renova a sessão pelo cookie. Chamadas simultâneas compartilham a mesma renovação. */
  const refresh = (): Promise<Session | null> => {
    refreshing ??= (async () => {
      try {
        let req: Request;
        if (store) {
          const token = store.get();
          if (!token) return null;
          req = new Request(opts.baseUrl + "/v1/auth/refresh", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ refresh_token: token }),
          });
        } else {
          req = new Request(opts.baseUrl + "/v1/auth/refresh", { method: "POST", credentials: "include" });
        }
        const res = await baseFetch(req);
        if (!res.ok) {
          setSession(null);
          return null;
        }
        const s = toSession((await res.json()) as AuthResponse);
        setSession(s);
        return s;
      } catch {
        return session; // falha de rede: mantém o estado e deixa a tela tratar
      } finally {
        refreshing = null;
      }
    })();
    return refreshing;
  };

  const authFetch = async (input: Request): Promise<Response> => {
    const isAuth = AUTH_PATHS.some((p) => new URL(input.url).pathname.startsWith(p));
    const retry = input.clone();
    const withToken = (req: Request) => {
      if (session) req.headers.set("Authorization", `Bearer ${session.accessToken}`);
      return req;
    };
    const res = await baseFetch(withToken(input));
    if (res.status !== 401 || isAuth) return res;
    const problem = (await res.clone().json().catch(() => ({}))) as Partial<Problem>;
    if (problem.code !== "session_invalid" && problem.code !== "unauthorized") return res;
    const renewed = await refresh();
    if (!renewed) return res;
    return baseFetch(withToken(retry));
  };

  // Site: cookies vão junto (refresh token HttpOnly). TV: nunca, só o token
  // no cabeçalho (a API recusa cookies vindos de origens não listadas).
  const client = createClient<paths>({
    baseUrl: opts.baseUrl,
    credentials: store ? "omit" : "include",
    fetch: authFetch,
  });

  /** Converte a resposta do openapi-fetch em dado ou ApiError. */
  async function unwrap<T>(p: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
    const { data, error, response } = await p;
    if (error !== undefined || !response.ok) throw new ApiError(response.status, error as Partial<Problem>);
    return data as T;
  }

  const device = opts.device ?? { kind: "web" as const, name: navigatorName() };

  const finishAuth = (r: AuthResponse): AuthResponse => {
    const s = toSession(r);
    if (s) setSession(s);
    return r;
  };

  return {
    client,
    unwrap,
    get session() {
      return session;
    },
    /** Na abertura do site: recupera a sessão pelo cookie, se houver. */
    restore: refresh,
    async login(email: string, password: string) {
      return finishAuth(await unwrap(client.POST("/v1/auth/login", { body: { email, password, device } })));
    },
    async verifyMfa(mfaToken: string, code: string) {
      return finishAuth(await unwrap(client.POST("/v1/auth/mfa/verify", { body: { mfa_token: mfaToken, code, device } })));
    },
    async setupMfa(mfaToken: string) {
      return unwrap(client.POST("/v1/auth/mfa/setup", { body: { mfa_token: mfaToken } }));
    },
    async confirmMfa(mfaToken: string, code: string) {
      return finishAuth(await unwrap(client.POST("/v1/auth/mfa/confirm", { body: { mfa_token: mfaToken, code, device } })));
    },
    async logout() {
      try {
        await client.POST("/v1/auth/logout");
      } finally {
        setSession(null);
      }
    },
    /** TV: pede o código para mostrar na tela. */
    async startDevice(name: string) {
      return unwrap(client.POST("/v1/auth/device", { body: { name } }));
    },
    /**
     * TV: consulta se já foi autorizada. Devolve a sessão quando autorizada,
     * ou lança ApiError (authorization_pending, slow_down, expired_token, access_denied).
     */
    async pollDevice(deviceCode: string) {
      return finishAuth(await unwrap(client.POST("/v1/auth/device/token", { body: { device_code: deviceCode } })));
    },
    /** Envia uma parte do vídeo (corpo binário; repetir é seguro). */
    async putUploadPart(id: string, n: number, body: Blob, signal?: AbortSignal) {
      await unwrap(
        client.PUT("/v1/admin/uploads/{id}/parts/{n}", {
          params: { path: { id, n } },
          body: body as unknown as string,
          bodySerializer: (b: unknown) => b as BodyInit,
          headers: { "Content-Type": "application/octet-stream" },
          signal,
        }),
      );
    },
    /** Depois de trocar a senha, renova para receber um token sem a marca de senha provisória. */
    async changePassword(current: string, next: string) {
      await unwrap(client.POST("/v1/me/password", { body: { current_password: current, new_password: next } }));
      await refresh();
    },
  };
}

export type Api = ReturnType<typeof createApi>;

function navigatorName(): string {
  if (typeof navigator === "undefined") return "";
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "Navegador";
  const os = /Windows/.test(ua) ? "Windows" : /Android/.test(ua) ? "Android" : /iPhone|iPad/.test(ua) ? "iOS" : /Mac OS/.test(ua) ? "macOS" : /Linux/.test(ua) ? "Linux" : "";
  return os ? `${browser} · ${os}` : browser;
}
